import React, { useRef, useState } from 'react';
import { Box, Grid, Typography, Stack, Accordion, AccordionSummary, AccordionDetails, TextField } from '@mui/material';
import MainCard from 'components/MainCard';
import LoadingButton from 'components/@extended/LoadingButton';
import readerImg from 'assets/images/analytics/reader.svg';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { SendSharp } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { coachSupport } from 'services/support/coachSupport';
import toast from 'react-hot-toast';
import { getUserByID } from 'services/users/getUserByID';

const faqs = [
  { q: 'How do I get started with Accupose AI Platform?', a: 'Click the "Get Started" button above...' },
  { q: 'Is my data secure on this platform?', a: 'Yes, we use industry-standard security protocols...' },
  { q: 'Can I invite my team members?', a: 'Absolutely! You can invite team members from your dashboard...' },
  { q: 'Where can I find tutorials or help resources?', a: 'Our Help Center is packed with guides and video tutorials...' },
  { q: 'How do I contact customer support?', a: 'You can reach out via the Contact Us form or email...' }
];

function Support() {
  const stepsRef = useRef(null);
  const [expanded, setExpanded] = useState(0);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);

  const userid = JSON.parse(localStorage.getItem('userid'));
  const roleid = JSON.parse(localStorage.getItem('roleid'));

  const { data: userDatas } = useQuery({
    queryFn: () => getUserByID({ userid, roleid }),
    queryKey: ['user-info', userid],
    enabled: !!userid && !!roleid
  });

  const { mutate: sendQueryMutation, isPending } = useMutation({
    mutationFn: coachSupport,
    onSuccess: (data) => {
      toast.success(data.message);
      setQuery('');
    },
    onError: (err) => {
      toast.error(err.message || 'Something went wrong.');
    }
  });

  const handleGetStarted = () => {
    stepsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAccordionChange = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleSendQuery = () => {
    if (!query.trim()) {
      toast.error('Please enter your query');
      return;
    }

    const userData = userDatas?.response;
    console.log('userData', userData);

    const senderName = userData?.username || 'Unknown';
    const senderEmail = userData?.emailaddress || 'unknown@example.com';
    const coachid = userData?.userid;
    const userAvatar = userData?.avatar ?? ''; // default fallback

    if (!coachid) {
      toast.error('User data not available');
      return;
    }

    sendQueryMutation({
      senderName,
      senderEmail,
      queryMessage: query,
      coachid,
      avatar: userAvatar
    });
  };

  return (
    <Box sx={{ minHeight: 'auto', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      {/* Hero Section */}
      <MainCard border={false} sx={{ mt: 4, p: { xs: 2, md: 4 }, bgcolor: (theme) => theme.palette.primary.darker }}>
        <Grid container spacing={4} alignItems="center" justifyContent="center">
          <Grid item xs={12} md={6}>
            <Stack spacing={2} alignItems="center">
              <Typography variant="h2" color="white" fontWeight={800} align="center" sx={{ mb: 2 }}>
                How can we help?
              </Typography>
              <Typography variant="h5" color="white" align="center">
                New To Accupose AI Platform?
              </Typography>
              <Typography variant="body1" color="white" align="center">
                Start your journey here with beginner-friendly resources that help you and your team work smarter.
              </Typography>
              <LoadingButton variant="contained" color="primary" size="large" onClick={handleGetStarted}>
                Get Started
              </LoadingButton>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} sx={{ textAlign: 'center' }}>
            <Box component="img" src={readerImg} alt="Support Illustration" sx={{ maxWidth: 320, width: '100%', mx: 'auto' }} />
          </Grid>
        </Grid>
      </MainCard>

      {/* FAQ Section */}
      <Box mt={6} mb={6} ref={stepsRef}>
        <MainCard
          title={
            <Typography variant="h4" color="primary" fontWeight={700}>
              Frequently Asked Questions
            </Typography>
          }
        >
          {faqs.map((faq, idx) => (
            <Accordion
              key={idx}
              expanded={expanded === idx}
              onChange={handleAccordionChange(idx)}
              sx={{
                mb: 2,
                boxShadow: 'none',
                border: '1px solid',
                borderColor: 'divider',
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary
                expandIcon={
                  expanded === idx ? (
                    <UpOutlined style={{ transform: 'rotate(-90deg)', transition: 'transform 0.2s' }} />
                  ) : (
                    <DownOutlined style={{ transform: 'rotate(0deg)', transition: 'transform 0.2s' }} />
                  )
                }
              >
                <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                  {faq.q}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" color="primary">
                  {faq.a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </MainCard>
      </Box>

      {/* More Queries Section */}
      <Box mb={8}>
        <MainCard>
          <Typography variant="h5" color="primary" fontWeight={700} mb={3}>
            More Queries?
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Type your question here"
              placeholder="Enter your query..."
              variant="outlined"
              fullWidth
              multiline
              disabled={isPending}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              minRows={3}
              sx={{
                '& .MuiOutlinedInput-root': { alignItems: 'flex-start' }
              }}
            />

            <LoadingButton
              variant="contained"
              color="primary"
              loading={isPending}
              disabled={isPending}
              onClick={handleSendQuery}
              startIcon={!sendQueryMutation.isLoading && <SendSharp />}
              sx={{
                width: { xs: '100%', sm: '120px' },
                alignSelf: 'flex-start'
              }}
            >
              {!sendQueryMutation.isLoading && 'Send'}
            </LoadingButton>
          </Stack>
        </MainCard>
      </Box>
    </Box>
  );
}

export default Support;
